/**
 * Visitor-session key for the SPA.
 *
 * The PHP sites have always carried a session: their server-side API client attaches an
 * X-SESSION-ID header, the API registers it in `visitor_session_info`, and every blog view
 * then lands in `statistics` (Blog_View) AND in the `session_article` bridge that ties an
 * order back to the articles its session read.
 *
 * The React sites never had one — `client.ts` sent only X-App-Id / X-App-Key. So
 * content_manager#GetOneBySlug resolved no session, took the `if ($visitorSessionId)`
 * branch nowhere, and akku-24 (app 5) and akkubolt (app 11) contributed ZERO rows to
 * session_article while apps 2/3/7/12 contributed 8 172. The two sites we invest the most
 * blog/GEO effort in were invisible to the funnel and to CM2's harvester.
 *
 * The key is generated client-side, exactly as the API expects (it keys
 * visitor_session_info on whatever X-SESSION-ID it receives, and runs bot detection before
 * storing anything).
 *
 * sessionStorage — not localStorage — is deliberate: it matches the lifetime of a PHP
 * browsing session, so "did this visit read an article before ordering?" stays the
 * question being answered. localStorage would merge unrelated visits weeks apart into one
 * session and silently inflate assisted-article attribution.
 */

const STORAGE_KEY = 'asos_session_key';

function randomKey(): string {
	// crypto.randomUUID needs a secure context; every production origin is https, but keep
	// a fallback so a plain-http preview cannot throw and break the whole API client.
	try {
		if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
			return crypto.randomUUID();
		}
		if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
			const b = new Uint8Array(16);
			crypto.getRandomValues(b);
			return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
		}
	} catch {
		/* fall through */
	}
	return `${Date.now().toString(16)}${Math.random().toString(16).slice(2, 14)}`;
}

/** Get-or-create the key for this browsing session. Safe on SSR/prerender (no window). */
export function getSessionKey(): string | null {
	try {
		if (typeof window === 'undefined' || !window.sessionStorage) return null;
		let key = window.sessionStorage.getItem(STORAGE_KEY);
		if (!key) {
			key = randomKey();
			window.sessionStorage.setItem(STORAGE_KEY, key);
		}
		return key;
	} catch {
		// Private-mode / storage-disabled browsers: degrade to no session rather than
		// throwing. Tracking is a nice-to-have; the page must still render.
		return null;
	}
}
